import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateInventoryAirline,
  useUpdateInventoryAirline,
} from '@/api/generated/inventory-airlines/inventory-airlines';
import {
  buildAirlineFormSchema,
  emptyAirlineFormValues,
  type AirlineFormValues,
} from './airlineFormSchema';
import { toAirlineRequestBody } from './toAirlineRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  OrganizationPartyId: 'organization',
  TradeName: 'tradeName',
  Iata: 'iata',
  Icao: 'icao',
  Callsign: 'callsign',
  Web: 'web',
  CurrencyId: 'currency',
  Status: 'status',
  Note: 'note',
  TicketTypes: 'ticketTypes',
  // Tři `bool` sloupce mají ve formuláři jedno pole — validátor jim proto dává vlastní jméno.
  PurchaseStrategies: 'purchaseStrategies',
  CheckInType: 'checkInType',
  CheckInWindowFromHours: 'checkInWindowFromHours',
  CheckInWindowToHours: 'checkInWindowToHours',
  // Blok podmínek změn jmen je v requestu vnořený, takže FluentValidation hlásí cesty s tečkou.
  'NameChangePolicy.FeeMode': 'nameChangeFeeMode',
  'NameChangePolicy.DeadlineValue': 'nameChangeDeadlineValue',
  'NameChangePolicy.NamelistValue': 'nameChangeNamelistValue',
  'NameChangePolicy.OnlineFee': 'nameChangeOnlineFee',
  'NameChangePolicy.AirportFee': 'nameChangeAirportFee',
};

interface UseAirlineFormOptions {
  /** Bez `airlineId` = zakládání, s `airlineId` = editace. */
  airlineId?: number;
  initialValues?: AirlineFormValues;
  /**
   * Voláno po úspěšném uložení — invalidace, toast; NEzavírá okno. `mode` odlišuje založení od
   * editace, protože okno se po založení musí překlopit do editace (jinak by další Save poslal
   * druhý POST a unikátnost IATA/ICAO ho odmítla konfliktem).
   */
  onSaved: (id: number, mode: 'created' | 'updated') => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole (konflikt kódů, business pravidla). */
  onError?: (error: unknown) => void;
}

/**
 * Formulář letecké společnosti: Zod + create/update mutace + mapování BE chyb.
 * `submit()` vrací `true` při úspěšném uložení.
 */
export function useAirlineForm({
  airlineId,
  initialValues,
  onSaved,
  onError,
}: UseAirlineFormOptions) {
  const { t } = useTranslation('inventory');
  const schema = useMemo(() => buildAirlineFormSchema(t), [t]);

  const createMutation = useCreateInventoryAirline();
  const updateMutation = useUpdateInventoryAirline();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptyAirlineFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      const data = toAirlineRequestBody(value);
      try {
        if (airlineId != null) {
          await updateMutation.mutateAsync({ id: airlineId, data });
          onSaved(airlineId, 'updated');
        } else {
          const newId = await createMutation.mutateAsync({ data });
          onSaved(newId, 'created');
        }
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + uložení; vrací `true` při úspěchu. */
  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  // Stav odesílání se nevrací zvlášť — patičku i tlačítka řídí `form.Subscribe`, aby existoval
  // jediný zdroj pravdy o tom, jestli formulář právě ukládá.
  return { form, submit };
}

/** Typ instance TanStack Form pro detail letecké společnosti (pro propsy podřízených komponent). */
export type AirlineFormApi = ReturnType<typeof useAirlineForm>['form'];
